import { CommonForm } from '@/components/CommonForm';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonPhoneInput } from '@/components/CommonForm/CommonPhoneInput';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';
import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useForm } from '@/hooks/useForm';
import { getClientLogger } from '@/lib/logger/pino-logger.client';
import { getDynamicsTopicConsents, isFormDynamicsLocationEnabled } from '@/tools/forms';
import { withSuspense } from '@/tools/withSuspense';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping, IDynamicsOptionSet } from '@/types/dynamicsForms';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RichText } from '@lamesarv-sdk/components';
import { useGetInventoryItem } from '@lamesarv-sdk/inventory';
import { navigate, parsePhone } from '@lamesarv-sdk/tools';
import { IInventory, InventoryField } from '@lamesarv-sdk/types';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { BaseCaptchaError, BaseOptInCheckbox, BaseSubmit, BaseSubscribeCheckbox } from '../base';
import { IFormProps, IFormResetterProps } from '../types';
import { IFormSchema, validationSchema } from './schema';

import * as FormClasses from '../classes';

interface IFormMetadata {
  stockNumber?: string;
  emailType?: string;
  details?: string[];
  formTitle?: string;
  formCustomComments?: string;
  inventoryItem?: IInventory;
}

const emailTypePrefix: { [key: string]: string } = {
  'best-price': 'Best Price for',
  'check-availability': 'Check availability for',
  'more-info': 'More Info for',
};

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ mobile: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export interface IFormContactInfoProps extends IFormProps {
  locationsOptionSet?: IDynamicsOptionSet;
  customLeadType?: string;
}

export const FormContactInfo = withSuspense((props: IFormContactInfoProps) => {
  const { context: layoutContext } = useContext(LayoutContext);
  const { env } = useEnv();
  const { fetchItem } = useGetInventoryItem({ skipFetch: true });
  const { isLoading, hasError, submitted, sendForm } = useForm();
  const searchParams = useSearchParams();

  const [formMetadata, setFormMetadata] = useState<IFormMetadata>({});

  const logger = getClientLogger('components.Forms.FormContactInfo');

  const getInventory = async (stockNumber: string): Promise<IInventory | undefined> => {
    try {
      const inventoryItem: IInventory = await fetchItem({ inventoryId: stockNumber });

      return inventoryItem;
    } catch (err) {
      logger.error(
        {
          event: 'Error fetching inventory item',
          stockNumber,
          error: err,
        },
        'Error fetching inventory item',
      );

      return;
    }
  };

  const getFormTitle = async (inventoryItem: IInventory, emailType?: string): Promise<string> => {
    const titlePrefix = (!!emailType && emailTypePrefix[emailType]) || emailTypePrefix['more-info'];

    return `${titlePrefix} ${inventoryItem[InventoryField.title]}`;
  };

  const getFormComments = async (inventoryItem: IInventory, details?: string[]): Promise<string | undefined> => {
    if (details?.includes('rebate')) {
      return `Interested in more information on $${inventoryItem[InventoryField.rebate].amount.toLocaleString()} rebate`;
    }

    return;
  };

  const getMetadata = async (): Promise<IFormMetadata> => {
    const stockNumber = searchParams.get('stockNumber') || undefined;
    const emailType = searchParams.get('emailType') || undefined;
    const details =
      searchParams
        .get('details')
        ?.split(',')
        ?.map((item) => item.trim())
        ?.filter(Boolean) || [];

    const inventoryItem = stockNumber ? await getInventory(stockNumber) : undefined;
    const formTitle = inventoryItem ? await getFormTitle(inventoryItem, emailType) : undefined;
    const formComments = inventoryItem ? await getFormComments(inventoryItem, details) : undefined;

    const metadata = {
      stockNumber,
      emailType,
      details,
      formTitle,
      formCustomComments: formComments,
      inventoryItem,
    };

    setFormMetadata(metadata);

    return metadata;
  };

  const getZipCode = (location: string): string => {
    const locationItem = layoutContext.inventoryLocations.find((inventoryLocation) =>
      inventoryLocation.aliases.includes(location.toLowerCase().trim()),
    );

    let zipCode = locationItem?.address?.trim().split(' ').pop() || '';

    if (zipCode.length !== 5) zipCode = '';

    return zipCode;
  };

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const zipCode = getZipCode(formInput.location);

    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_leadtype', DataverseFieldValue: String(DynamicsLeadType.seller) },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      {
        DataverseFieldName: 'lmrv_stocknumber',
        DataverseFieldValue: formMetadata?.stockNumber ? formMetadata?.stockNumber?.toLowerCase() : '',
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: formInput.firstName },
      { DataverseFieldName: 'lastname', DataverseFieldValue: formInput.lastName },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: formInput.email },
      { DataverseFieldName: 'address1_postalcode', DataverseFieldValue: zipCode },
      { DataverseFieldName: 'lmrv_comments', DataverseFieldValue: formInput.comments || '' },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.mobile) },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue:
          String(props.locationsOptionSet.find((item) => item.Label === formInput.location)?.DataverseValue) || '',
      },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    if (formInput.subscribe || formInput.smsOptIn) {
      const topicChannels = [formInput.subscribe ? 'Email' : undefined, formInput.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    if (isFormDynamicsLocationEnabled(env, formInput.location)) {
      await sendForm({
        formKey: 'CONTACT_INFO',
        formName: props.custom_name || 'Form Contact Info',
        mappings,
      });
    }
  };

  const wasSuccessful = !isLoading && !hasError && submitted;

  useEffect(() => {
    getMetadata();
  }, [searchParams]);

  return (
    <CommonForm
      id={props.custom_id || 'formContactInfo'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-contact-info` : 'form-contact-info'}
      customCaptchaErrorMessage={() => <BaseCaptchaError />}
    >
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge('w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1', props.classes_header)}
        >
          {formMetadata?.formTitle ||
            (props.title && (
              <div className={twMerge('text-2xl text-sage-800 font-bold uppercase', props.classes_title)}>
                {formMetadata?.formTitle || props.title}
              </div>
            ))}
          {props.description && (
            <div className={twMerge('text-lg text-sage-800', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-1 bg-white', props.classes_container)}>
        {!props.extract_header && (
          <div className={twMerge('flex flex-col gap-4', props.classes_header)}>
            {formMetadata?.formTitle ||
              (props.title && (
                <div className={twMerge('text-2xl text-sage-800 font-bold uppercase', props.classes_title)}>
                  {formMetadata?.formTitle || props.title}
                </div>
              ))}
            {props.description && (
              <div className={twMerge('text-lg text-sage-800', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </div>
        )}
        <div className={twMerge('flex flex-col', props.classes_content)}>
          <div className="flex flex-row flex-wrap -mx-2">
            {isLoading && (
              <div
                className={
                  'flex items-center w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 mx-2 shadow-md'
                }
                role="alert"
              >
                <FontAwesomeIcon icon={faClock} className="w-8 mr-2" />
                <span>Sending...</span>
              </div>
            )}
            {wasSuccessful && (
              <div
                className={
                  'flex items-center w-full bg-green-100 border border-green-500 text-green-700 py-2 px-3 mb-4 mx-2 shadow-md'
                }
                role="alert"
              >
                <FontAwesomeIcon icon={faCheckCircle} className="w-8 mr-2" />
                <span
                  data-tag-group="form-success-label"
                  data-tag={
                    props.data_tag
                      ? `${props.data_tag}--form-contact-info-success-label`
                      : 'form-contact-info-success-label'
                  }
                >
                  Thank you for requesting more information.
                </span>
              </div>
            )}
            {!isLoading && hasError && (
              <div
                className={
                  'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 mx-2 shadow-md'
                }
                role="alert"
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
                <span>An error has occurred, please try again later or contact us using alternative methods.</span>
              </div>
            )}
            <div className="mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Personal Information</div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                label="First Name"
                placeholder="First Name"
                name="firstName"
                isRequired
                classContainer="flex-1"
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                label="Last Name"
                placeholder="Last Name"
                name="lastName"
                isRequired
                classContainer="flex-1"
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap -mx-2">
            <div className="mt-3 mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Contact Information</div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonPhoneInput label="Mobile Phone" placeholder="Mobile Phone" name="mobile" classContainer="flex-1" />
            </div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonInput
                type="email"
                label="Email"
                placeholder="Email"
                name="email"
                isRequired
                classContainer="flex-1"
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonLocationSelect
                label="Select store location"
                classContainer="flex-1"
                locationsOptionSet={props.locationsOptionSet}
                isRequired
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonTextarea label="Comments" name="comments" />
            </div>
            {(layoutContext.detailsSettings.displaySubscribeCheckbox || props.display_subscribe_checkbox) && (
              <div className="my-2 px-2 w-full">
                <BaseSubscribeCheckbox
                  htmlContent={layoutContext.detailsSettings.subscribeCheckboxContent || props.custom_subscribe_text}
                />
              </div>
            )}
            {(layoutContext.detailsSettings.displaySmsCheckbox || props.display_sms_checkbox) && (
              <div className="my-2 px-2 w-full">
                <BaseOptInCheckbox
                  name="smsOptIn"
                  nameDependency="mobile"
                  htmlContent={layoutContext.detailsSettings.customSmsText || props.custom_sms_text}
                />
              </div>
            )}
          </div>
          <div className="flex flex-row">
            <div className="mt-2">
              <BaseSubmit {...props} formNameSlug="form-contact-info" styleClasses={FormClasses.buttonNormalClasses} />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
